import { create } from "zustand";

/**
 * FR-015-091, § Overlays, Toast: the four variants, at most three visible with the rest
 * queued, an action slot, the request id with a copy control, and error toasts that never
 * auto-dismiss. The store holds message keys and values rather than rendered text, so the
 * region renders every string through the catalogue.
 */
export type ToastVariant = "info" | "success" | "warning" | "error";

export interface ToastAction {
  readonly label: string;
  readonly onSelect: () => void;
}

export interface ToastInput {
  readonly variant: ToastVariant;
  readonly title: string;
  readonly description?: string;
  readonly action?: ToastAction;
  /** Milliseconds; 6 s when absent, 10 s when the toast carries an action, and an error toast ignores it and never auto-dismisses. */
  readonly duration?: number;
  readonly requestId?: string;
}

export interface Toast extends ToastInput {
  readonly id: string;
  readonly createdAt: number;
}

export const MAX_VISIBLE_TOASTS = 3;
export const DEFAULT_TOAST_DURATION_MS = 6000;
/** A toast carrying an action (Undo, Restore, Open) waits longer, so the action can be read and reached before it goes (2026-10-10). */
export const ACTION_TOAST_DURATION_MS = 10_000;

export interface ToastState {
  readonly toasts: readonly Toast[];
  readonly push: (input: ToastInput) => string;
  readonly dismiss: (id: string) => void;
  readonly clear: () => void;
}

let sequence = 0;

export const useToastStore = create<ToastState>((set) => ({
  toasts: [],
  push(input) {
    sequence += 1;
    const id = `toast-${sequence}`;
    set((state) => ({ toasts: [...state.toasts, { ...input, id, createdAt: Date.now() }] }));
    return id;
  },
  dismiss(id) {
    set((state) => ({ toasts: state.toasts.filter((toast) => toast.id !== id) }));
  },
  clear() {
    set({ toasts: [] });
  },
}));

/** The three at the front of the queue are visible; the rest wait their turn. */
export function visibleToasts(toasts: readonly Toast[]): readonly Toast[] {
  return toasts.slice(0, MAX_VISIBLE_TOASTS);
}

export function durationFor(toast: Toast): number | null {
  if (toast.variant === "error") return null;
  return toast.duration ?? (toast.action === undefined ? DEFAULT_TOAST_DURATION_MS : ACTION_TOAST_DURATION_MS);
}

/** Push from anywhere outside React: the runtime's dropped-write toast, the reducer's notices. */
export const toast = (input: ToastInput): string => useToastStore.getState().push(input);
